import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Eye, MessageSquare, CheckCircle, Trash2, Loader, Mail, User, FileText, Calendar } from 'lucide-react';
import { toast } from 'react-hot-toast';
import apiService from '../../services/api';
import { useApiData } from '../../hooks/useApi';
import Modal from '../common/Modal';

const Enquiries = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showViewModal, setShowViewModal] = useState(false);
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [deletingEnquiry, setDeletingEnquiry] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');

  const { data: enquiriesData, loading, refetch } = useApiData(
    () => apiService.getEnquiries({ search: searchTerm, status: statusFilter }),
    [searchTerm, statusFilter]
  );

  const enquiries = Array.isArray(enquiriesData?.data) ? enquiriesData.data :
    Array.isArray(enquiriesData) ? enquiriesData : [];

  const handleView = (enquiry) => {
    setSelectedEnquiry(enquiry);
    setShowViewModal(true);
  };

  const handleReply = (enquiry) => {
    setSelectedEnquiry(enquiry);
    setReplyMessage('');
    setShowReplyModal(true);
  };

  const handleMarkResolved = async (enquiry) => {
    try {
      await fetch(`/api/enquiries/${enquiry.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ status: 'replied' })
      });
      toast.success('Enquiry marked as resolved');
      refetch();
    } catch (error) {
      toast.error('Failed to update enquiry status');
    }
  };

  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!replyMessage.trim()) {
      toast.error('Please enter a reply message');
      return;
    }
    
    try {
      await apiService.post(`/enquiries/${selectedEnquiry.id}/reply`, {
        message: replyMessage,
        recipientEmail: selectedEnquiry.email,
        recipientName: selectedEnquiry.name,
        originalSubject: selectedEnquiry.subject
      });
      
      toast.success('Reply sent successfully via email');
      setShowReplyModal(false);
      setReplyMessage('');
      refetch();
    } catch (error) {
      console.error('Reply error:', error);
      toast.error('Failed to send reply');
    }
  };

  const handleDelete = (enquiry) => {
    setDeletingEnquiry(enquiry);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await apiService.deleteEnquiry(deletingEnquiry.id);
      toast.success('Enquiry deleted successfully');
      refetch();
    } catch (error) {
      toast.error(error.message || 'Failed to delete enquiry');
    } finally {
      setShowDeleteModal(false);
      setDeletingEnquiry(null);
    }
  };

  if (loading) {
    return (
      <div className="responsive-card">
        <div className="responsive-loading">
          <div className="responsive-spinner" />
          <p>Loading enquiries...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="content-section">
      <div className="section-header">
        <h2>General Enquiries</h2>
        <div className="enquiry-stats">
          <span className="stat-badge">{enquiriesData?.newCount || 0} New</span>
          <span className="stat-badge">{enquiriesData?.pendingCount || 0} Pending</span>
        </div>
      </div>

      <div className="filters">
        <input
          type="text"
          placeholder="Search enquiries..."
          className="search-input"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ flex: 1 }}
        />
        <select
          className="filter-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All Status</option>
          <option value="new">New</option>
          <option value="pending">Pending</option>
          <option value="replied">Replied</option>
        </select>
      </div>

      <div className="table-container table-responsive">
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Contact Number</th>
              <th>Subject</th>
              <th>Date</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {enquiries.map((enquiry, index) => (
              <motion.tr
                key={enquiry.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}
              >
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div className="user-avatar" style={{ width: '32px', height: '32px', fontSize: '0.75rem' }}>
                      {enquiry.name?.split(' ').filter(n => n).map(n => n[0]).join('') || '?'}
                    </div>
                    <span>{enquiry.name}</span>
                  </div>
                </td>
                <td>{enquiry.email}</td>
                <td>{enquiry.phone}</td>
                <td>{enquiry.subject}</td>
                <td>
                  {new Date(enquiry.created_at || enquiry.updated_at).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric"
                  })}
                </td>
                <td>
                  <span className={`status ${enquiry.status}`}>
                    {enquiry.status}
                  </span>
                </td>
                <td>
                  <div className="action-buttons">
                    <motion.button
                      className="btn-sm touch-target"
                      onClick={() => handleView(enquiry)}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      title="View Details"
                    >
                      <Eye size={12} />
                    </motion.button>
                    <motion.button
                      className="btn-sm primary touch-target"
                      onClick={() => handleReply(enquiry)}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      title="Reply"
                    >
                      <MessageSquare size={12} />
                    </motion.button>
                    <motion.button
                      className="btn-sm success touch-target"
                      onClick={() => handleMarkResolved(enquiry)}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      title="Mark as Resolved"
                      disabled={enquiry.status === 'replied'}
                    >
                      <CheckCircle size={12} />
                    </motion.button>
                    <motion.button
                      className="btn-sm danger touch-target"
                      onClick={() => handleDelete(enquiry)}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      title="Delete"
                    >
                      <Trash2 size={12} />
                    </motion.button>
                  </div>
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* View Modal */}
      <Modal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        title="Enquiry Details"
      >
        {selectedEnquiry && (
          <div className="admission-details-enhanced">
            {/* Header Section with Avatar and Status */}
            <div className="details-header">
              <div className="student-info">
                <div className="student-avatar">
                  {selectedEnquiry.name?.split(' ').filter(n => n).map(n => n[0]).join('') || '?'}
                </div>
                <div className="student-meta">
                  <h3>{selectedEnquiry.name}</h3>
                  <span className={`status-badge ${selectedEnquiry.status?.toLowerCase().replace(/\s+/g, '-')}`}>
                    {selectedEnquiry.status}
                  </span>
                </div>
              </div>
              <div className="application-date">
                <Calendar size={16} />
                <span>Received on {new Date(selectedEnquiry.created_at || selectedEnquiry.updated_at).toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "long",
                  year: "numeric"
                })}</span>
              </div>
            </div>

            {/* Details Grid */}
            <div className="details-grid">
              <div className="detail-card">
                <div className="detail-icon">
                  <Mail size={18} />
                </div>
                <div className="detail-content">
                  <label>Email Address</label>
                  <p>{selectedEnquiry.email || 'Not provided'}</p>
                </div>
              </div>

              <div className="detail-card">
                <div className="detail-icon">
                  <MessageSquare size={18} />
                </div>
                <div className="detail-content">
                  <label>Subject</label>
                  <p>{selectedEnquiry.subject || 'No subject'}</p>
                </div>
              </div>

              <div className="detail-card" style={{ gridColumn: '1 / -1' }}>
                <div className="detail-icon">
                  <Mail size={18} />
                </div>
                <div className="detail-content">
                  <label>Contact Number</label>
                  <p>{selectedEnquiry.phone || 'Not provided'}</p>
                </div>
              </div>

              <div className="detail-card" style={{ gridColumn: '1 / -1' }}>
                <div className="detail-icon">
                  <FileText size={18} />
                </div>
                <div className="detail-content">
                  <label>Message</label>
                  <p style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>{selectedEnquiry.message || 'No message provided'}</p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="details-actions">
              <button
                className="btn-success"
                onClick={() => {
                  setShowViewModal(false);
                  handleReply(selectedEnquiry);
                }}
              >
                <MessageSquare size={16} />
                Reply to Enquiry
              </button>
              <button
                className="btn-danger"
                onClick={() => {
                  setShowViewModal(false);
                  handleMarkResolved(selectedEnquiry);
                }}
                disabled={selectedEnquiry.status === 'replied'}
              >
                <CheckCircle size={16} />
                Mark as Resolved
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Reply Modal */}
      <Modal
        isOpen={showReplyModal}
        onClose={() => setShowReplyModal(false)}
        title="Reply to Enquiry"
      >
        {selectedEnquiry && (
          <form onSubmit={handleSendReply} className="modal-form">
            {/* Recipient Info Card */}
            <div className="detail-card" style={{ marginBottom: '1.5rem' }}>
              <div className="detail-icon">
                <User size={18} />
              </div>
              <div className="detail-content">
                <label>Recipient</label>
                <p><strong>{selectedEnquiry.name}</strong> ({selectedEnquiry.email})</p>
              </div>
            </div>
            
            {/* Subject Info Card */}
            <div className="detail-card" style={{ marginBottom: '1.5rem' }}>
              <div className="detail-icon">
                <MessageSquare size={18} />
              </div>
              <div className="detail-content">
                <label>Subject</label>
                <p>Re: {selectedEnquiry.subject}</p>
              </div>
            </div>
            
            {/* Original Message Reference */}
            <div className="form-group" style={{ marginBottom: '1.5rem' }}>
              <label style={{ fontSize: '0.875rem', fontWeight: '600', color: '#6b7280', marginBottom: '0.5rem', display: 'block' }}>Original Message:</label>
              <div style={{ 
                background: '#f9fafb', 
                border: '1px solid #e5e7eb', 
                borderRadius: '0.75rem', 
                padding: '1rem',
                fontSize: '0.875rem',
                color: '#4b5563',
                maxHeight: '100px',
                overflowY: 'auto'
              }}>
                {selectedEnquiry.message}
              </div>
            </div>
            
            {/* Reply Message */}
            <div className="form-group">
              <label>Your Reply *</label>
              <textarea
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
                rows="6"
                placeholder="Type your professional reply here..."
                required
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.75rem',
                  fontSize: '0.875rem',
                  lineHeight: '1.5',
                  resize: 'vertical',
                  minHeight: '120px'
                }}
              />
            </div>
            
            <div className="form-actions" style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
              <button 
                type="button" 
                className="btn-sm"
                onClick={() => setShowReplyModal(false)}
                style={{
                  padding: '0.5rem 1rem',
                  background: '#f3f4f6',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.5rem',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="btn-primary"
                style={{
                  padding: '0.5rem 1.5rem',
                  background: 'linear-gradient(135deg, #ed0973 0%, #f43f93 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.5rem',
                  cursor: 'pointer',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <Mail size={16} />
                Send Email Reply
              </button>
            </div>
          </form>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDeletingEnquiry(null);
        }}
        title="Confirm Delete"
      >
        <div style={{ textAlign: 'center', padding: '1rem 0' }}>
          <p>Are you sure you want to delete this enquiry?</p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginTop: '1.5rem' }}>
            <button
              className="btn-sm danger"
              onClick={confirmDelete}
            >
              Delete
            </button>
            <button
              className="btn-sm"
              onClick={() => {
                setShowDeleteModal(false);
                setDeletingEnquiry(null);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Enquiries;